import * as d3 from 'd3'

import {
  MENU_VIEW_MODES,
  DOT_RADIUS,
  DOT_STROKE_WIDTH,
  DOT_STYLE,
  HEATMAP_COLORS,
  HEATMAP_OPACITY,
  OVERLAY_OPACITY,
  HEATMAP_IMAGE_OPACITY,
  SECTION_STYLE
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/constants'
import { sectionTitle } from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/text'

// the id is an export contract: pptx.coffee (getMenuTestParams -> formatSVG) clones this
// node out of the live DOM to build the PPTX composite
export const overlayId = id => `svg-mn-heatmap-${id}`

export const OVERLAY_CLASS = 'svg-mn-heatmap'

// viewBox keeps coordinates in original image space; width/height scale the SVG to match the
// scaled image. Shared by the live chart, whose svg React renders, and the export, which builds its own.
export const overlaySize = ({ width, height, scale }) => ({
  width: Math.round(width * scale),
  height: Math.round(height * scale),
  viewBox: `0 0 ${width} ${height}`
})

// the plain heatmap dims the layer over the image; the other modes draw it at full strength
export const overlayOpacity = mode => (MENU_VIEW_MODES.HEATMAP === mode ? HEATMAP_IMAGE_OPACITY : null)

// the live svg belongs to React, so taking the drawing down empties it rather than removing it
export const clearOverlay = svg => d3.select(svg).selectAll('*').remove()

// The export builds its overlay detached, so it creates the svg here. The style is written as one
// attribute so it survives the export clone (formatSVG), which leaves the page stylesheets behind.
const createOverlaySvg = (container, { id, width, height, scale, mode }) => {
  const opacity = overlayOpacity(mode)
  const { viewBox, ...size } = overlaySize({ width, height, scale })

  return container
    .append('svg:svg')
    .attr('id', overlayId(id))
    .attr('width', size.width)
    .attr('height', size.height)
    .attr('viewBox', viewBox)
    .attr('class', OVERLAY_CLASS)
    .attr('style', `position: absolute; top: 0; left: 0;${null == opacity ? '' : ` opacity: ${opacity};`}`)
}

const drawHeatmap = (svg, { heatmap, mode }) => {
  const { contours, levels } = heatmap

  if (!contours || !levels) return

  const minLevel = d3.min(levels)
  const maxLevel = d3.max(levels)

  const colorsStops = HEATMAP_COLORS.map((_, i) => minLevel + (i / (HEATMAP_COLORS.length - 1)) * (maxLevel - minLevel))

  const opacityValues = MENU_VIEW_MODES.HEATMAP === mode ? HEATMAP_OPACITY : OVERLAY_OPACITY

  const getOpacity = d3.scaleLinear().domain([minLevel, maxLevel]).range(opacityValues)
  const getColor = d3.scaleLinear().domain(colorsStops).range(HEATMAP_COLORS)

  const line = d3
    .line()
    .x(d => d.x)
    .y(d => d.y)

  if (!contours.length) return

  contours.forEach(item => {
    const points = item.contour.map(point => ({ x: point.x, y: point.y }))

    svg
      .append('svg:path')
      .attr('d', line(points))
      .attr('fill', `${getColor(item.level)}`)
      .attr('fill-opacity', `${getOpacity(item.level)}`)
  })
}

// click dots on the menu image
const drawDots = (svg, { heatmap, scale }) => {
  if (!heatmap?.points) return

  // fixed screen-pixel size: compensate for viewBox -> element scaling
  const r = DOT_RADIUS / scale
  const sw = DOT_STROKE_WIDTH / scale

  heatmap.points.forEach(point => {
    svg
      .append('svg:circle')
      .attr('cx', point.x)
      .attr('cy', point.y)
      .attr('r', r)
      .attr('style', `${DOT_STYLE} stroke-width: ${sw};`)
  })
}

// section outlines on the plain image (same SVG layer as heatmap / dots)
const drawSections = (svg, { sections }) => {
  const bordered = (sections || []).filter(s => s?.border?.type)
  if (!bordered.length) return

  bordered.forEach(section => {
    const border = section.border
    let shape

    switch (border.type) {
      case 'rect':
        shape = svg
          .append('svg:rect')
          .attr('x', border.x)
          .attr('y', border.y)
          .attr('width', border.width)
          .attr('height', border.height)
        break
      case 'ellipse':
        shape = svg
          .append('svg:ellipse')
          .attr('cx', border.cx)
          .attr('cy', border.cy)
          .attr('rx', border.rx)
          .attr('ry', border.ry)
        break
      case 'polygon':
        shape = svg.append('svg:polygon').attr('points', (border.points || []).map(p => p.join(',')).join(' '))
        break
      default:
        return
    }

    // transparent (not none) fill keeps the whole area hoverable for the native title;
    // inline base stroke survives the export clone (like contours/dots); opacity + hover live in CSS
    shape
      .classed('mn-section-area', true)
      .attr('style', SECTION_STYLE)
      .append('svg:title')
      .text(sectionTitle(section.text || ''))
  })
}

// draws the view mode's layer into an svg that already exists -- the live chart's, which React renders
export const drawOverlayInto = (svg, options) => {
  const { mode } = options

  if (MENU_VIEW_MODES.HEATMAP === mode || MENU_VIEW_MODES.SHEATMAP === mode) {
    drawHeatmap(svg, options)
  } else if (MENU_VIEW_MODES.CLICKS === mode) {
    drawDots(svg, options)
  } else if (MENU_VIEW_MODES.PLAIN === mode) {
    drawSections(svg, options)
  }

  return svg
}

// creates the svg and draws into it -- the export's path, which has no React tree to render one
export const drawOverlay = (container, options) => drawOverlayInto(createOverlaySvg(container, options), options)
